import { Link } from "react-router-dom";
import { Trash2, Edit, Sparkles, RotateCw, ArrowUpToLine } from "lucide-react";
import { formatPrice, formatDate, isFeatured, isExpired, daysLeft, canRepost } from "@/lib/api";

const HEADERS = [
  ["Title", ""], ["Category", "hidden md:table-cell"], ["Price", ""],
  ["Views", "hidden md:table-cell"], ["Posted", "hidden md:table-cell"], ["Status", ""],
];

function ExpiryStatus({ listing }) {
  const id = listing.listing_id;
  if (isExpired(listing)) {
    return <span data-testid={`expiry-status-${id}`} className="inline-block bg-[#FF0000] text-white px-2 py-0.5 text-[10px] uppercase font-bold">Expired</span>;
  }
  const left = daysLeft(listing);
  return <span data-testid={`expiry-status-${id}`} className={`text-xs uppercase ${left <= 3 ? "text-[#FF0000] font-bold" : "text-gray-600"}`}>{left}d left</span>;
}

function RowActions({ listing, onPromote, onDelete, onRepost, onBump }) {
  const id = listing.listing_id;
  return (
    <td className="p-3 text-right space-x-1 whitespace-nowrap [&>*]:align-middle">
      {canRepost(listing) && (
        <button data-testid={`repost-btn-${id}`} onClick={() => onRepost(id)} className="border-2 border-black bg-yellow-300 px-2 py-1 text-[10px] uppercase font-bold hover:bg-black hover:text-white inline-flex items-center gap-1 align-middle"><RotateCw size={12} /> Repost</button>
      )}
      {!isExpired(listing) && (
        <button data-testid={`bump-btn-${id}`} onClick={() => onBump(id)} className="border-2 border-black p-1.5 hover:bg-yellow-300" title="Bump to top (paid)"><ArrowUpToLine size={12} /></button>
      )}
      {isFeatured(listing) ? (
        <span className="inline-block bg-[#0000EE] text-white border border-[#0000EE] px-2 py-1 font-mono-data text-[10px] uppercase font-bold mr-1">★ Featured</span>
      ) : (
        <button data-testid={`promote-btn-${id}`} onClick={() => onPromote(id)} className="border-2 border-[#0000EE] text-[#0000EE] p-1.5 hover:bg-[#0000EE] hover:text-white" title="Promote (paid)"><Sparkles size={12} /></button>
      )}
      <Link to={`/edit/${id}`} data-testid={`edit-btn-${id}`} className="inline-block border-2 border-black p-1.5 hover:bg-black hover:text-white text-black no-underline"><Edit size={12} /></Link>
      <button data-testid={`delete-btn-${id}`} onClick={() => onDelete(id)} className="border-2 border-black p-1.5 hover:bg-[#FF0000] hover:text-white hover:border-[#FF0000]"><Trash2 size={12} /></button>
    </td>
  );
}

function ListingTableRow({ listing, onPromote, onDelete, onRepost, onBump }) {
  return (
    <tr className="border-t border-black" data-testid={`my-listing-row-${listing.listing_id}`}>
      <td className="p-3"><Link to={`/listing/${listing.listing_id}`} className="link-blue">{listing.title}</Link></td>
      <td className="p-3 hidden md:table-cell uppercase text-xs">{listing.category?.replace("-", " ")}</td>
      <td className="p-3 font-bold">{formatPrice(listing.price, listing.currency)}</td>
      <td className="p-3 hidden md:table-cell">{listing.views || 0}</td>
      <td className="p-3 hidden md:table-cell uppercase text-xs text-gray-600">{formatDate(listing.created_at)}</td>
      <td className="p-3"><ExpiryStatus listing={listing} /></td>
      <RowActions listing={listing} onPromote={onPromote} onDelete={onDelete} onRepost={onRepost} onBump={onBump} />
    </tr>
  );
}

export function MyListingsTable({ listings, onPromote, onDelete, onRepost, onBump }) {
  if (listings.length === 0) {
    return <div className="border-2 border-black p-12 text-center font-mono-data text-sm uppercase text-gray-500 mt-4">No listings yet. <Link to="/post" className="link-blue">Post your first ad →</Link></div>;
  }
  return (
    <div className="border-2 border-black mt-4 overflow-x-auto">
      <table className="w-full text-sm font-mono-data" data-testid="my-listings-table">
        <thead className="bg-black text-white text-xs uppercase">
          <tr>
            {HEADERS.map(([label, cls]) => <th key={label} className={`text-left p-3 ${cls}`}>{label}</th>)}
            <th className="text-right p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {listings.map((l) => <ListingTableRow key={l.listing_id} listing={l} onPromote={onPromote} onDelete={onDelete} onRepost={onRepost} onBump={onBump} />)}
        </tbody>
      </table>
    </div>
  );
}
